import { Deliverable } from '@/types/deliverables';

/**
 * Normalizes the various deliverables API response shapes into a flat Deliverable[].
 *
 * Handles:
 *   - bare array:                       [ ... ]
 *   - standard list endpoint:           { data: [ ... ] }
 *   - by-lead endpoint:                 { data: { lead, deliverables: [ ... ], stats } }
 *   - top-level deliverables wrapper:   { deliverables: [ ... ] }
 *   - anything else / null / malformed: []
 *
 * The /deliverables/by-lead/:leadId endpoint returns 200 with
 * `{ success, data: { lead, deliverables, stats } }`, so a naive `result.data || result`
 * yields an OBJECT instead of an array and crashes `.filter()` in the consuming component.
 */
/**
 * Generic normalizer that pulls an array out of the many CRM API response shapes.
 *
 * Resolution order (first match wins):
 *   1. `result` itself, if it is already an array            -> [ ... ]
 *   2. `result.data`, if it is an array                      -> { data: [ ... ] }
 *   3. `result.data[key]`, if `key` given and it is an array -> { data: { <key>: [ ... ] } }
 *   4. `result[key]`, if `key` given and it is an array      -> { <key>: [ ... ] }
 *   5. otherwise                                             -> []
 *
 * Several endpoints return `data` as an OBJECT (e.g.
 * `/leads/won-without-deliverables` -> { data: { leads, count } }), so the naive
 * `result.data || result` yields an object and crashes `.slice()`/`.length` in the
 * consuming component. Passing the `key` ('leads', 'deliverables', ...) extracts the
 * nested array safely; calling without a key is a no-op for already-array responses.
 */
export function extractArray<T = any>(result: unknown, key?: string): T[] {
  // 1. Already an array
  if (Array.isArray(result)) {
    return result as T[];
  }

  if (result && typeof result === 'object') {
    const obj = result as Record<string, unknown>;

    // 2. { data: [...] }
    if (Array.isArray(obj.data)) {
      return obj.data as T[];
    }

    // 3. { data: { <key>: [...] } }
    if (key && obj.data && typeof obj.data === 'object') {
      const dataObj = obj.data as Record<string, unknown>;
      if (Array.isArray(dataObj[key])) {
        return dataObj[key] as T[];
      }
    }

    // 4. { <key>: [...] }
    if (key && Array.isArray(obj[key])) {
      return obj[key] as T[];
    }
  }

  // 5. Fallback
  return [];
}

export function toDeliverablesArray(result: unknown): Deliverable[] {
  return extractArray<Deliverable>(result, 'deliverables');
}
